'use client'

// MUI Imports
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import Skeleton from '@mui/material/Skeleton'
import { useTheme } from '@mui/material/styles'

// Types
import type { QuickInsightsWidgetProps } from '@/types/dashboards/resumeTypes'

// Components
import IconifyIcon from '@/components/layout/shared/Icons'

// Hooks
import { useDashboardResumeTranslations } from '@/hooks/useTranslations'

// Types para componente interno
interface InsightData {
  title: string
  value: string | number
  trend: number
  icon: string
  color: 'success' | 'warning' | 'error' | 'info'
}

interface AlertData {
  type: 'warning' | 'error' | 'info'
  title: string
  message: string
  originalTitle?: string
}

interface ProgressData {
  label: string
  current: number
  target: number
  unit: string
}

// Componente Skeleton
const InsightsSkeleton = () => (
  <Card sx={{ height: '100%', minHeight: 630 }}>
    <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
      <Stack spacing={2} sx={{ flex: 1 }}>
        {[...Array(6)].map((_, index) => (
          <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Skeleton variant="text" width={100} height={16} />
            <Skeleton variant="text" width={60} height={16} />
          </Box>
        ))}
        <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1, mt: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
      </Stack>
    </CardContent>
  </Card>
)

const QuickInsightsWidget = ({
  data,
  loading = false
}: QuickInsightsWidgetProps) => {
  const { t } = useDashboardResumeTranslations()
  const theme = useTheme()

  // Mapear dados da API ou usar dados padrão
  const insightsData: InsightData[] = data?.keyIndicators?.map(indicator => ({
    title: t(`insights.${indicator.title}`),
    value: indicator.value,
    trend: indicator.trend,
    icon: indicator.icon,
    color: indicator.color as 'success' | 'warning' | 'error' | 'info'
  })) || []

    // Criar alertas baseados nos keyIndicators e alerts da API
  const createAlertsFromData = (): AlertData[] => {
    const alerts: AlertData[] = []

    // Verificar se há pagamentos vencidos nos keyIndicators
    const overduePaymentsIndicator = data?.keyIndicators?.find(indicator =>
      indicator.title === 'overduePayments'
    )

    if (overduePaymentsIndicator) {
      alerts.push({
        type: 'error' as const,
        title: t('insights.overduePayments'),
        message: `${overduePaymentsIndicator.value} vencidos`,
        originalTitle: 'overduePayments'
      })
    }

    // Adicionar alertas da API (apenas pagamentos e saldo)
    const apiAlerts = data?.alerts?.filter(alert =>
      alert.title === 'upcomingPayments7Days' || alert.title === 'accountBalance'
    ).map(alert => ({
      type: alert.severity as 'warning' | 'error' | 'info',
      title: t(`insights.${alert.title}`),
      message: alert.message,
      originalTitle: alert.title
    })) || []

    return [...alerts, ...apiAlerts]
  }

  const alertsData = createAlertsFromData()

    // Mapear progresso das metas da API (apenas metas não zeradas)
  const progressData: ProgressData[] = data?.goalsProgress?.filter(goal =>
    goal.current > 0 && goal.target > 0
  ).map(goal => ({
    label: t(`insights.${goal.title}`),
    current: goal.current,
    target: goal.target,
    unit: goal.unit
  })) || []

  if (loading) {
    return <InsightsSkeleton />
  }

  return (
    <Card sx={{ height: '100%', minHeight: 630 }}>
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          {t('insights.title')}
        </Typography>

        <Stack spacing={2} sx={{ flex: 1 }}>
          {/* Alertas - Movidos para o topo */}
          {alertsData.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.75rem' }}>
                {t('insights.alerts')}
              </Typography>
              <Stack spacing={1}>
                {alertsData.map((alert, index) => {
                  const isPaymentAlert = alert.originalTitle === 'upcomingPayments7Days' || alert.originalTitle === 'overduePayments'

                  // Definir ícone baseado no tipo de alerta
                  let alertIcon = 'tabler:wallet' // padrão para saldo

                  if (alert.originalTitle === 'upcomingPayments7Days') {
                    alertIcon = 'tabler:calendar-due'
                  } else if (alert.originalTitle === 'overduePayments') {
                    alertIcon = 'tabler:alert-circle'
                  }

                  return (
                    <Alert
                      key={index}
                      severity={alert.type}
                      icon={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24,
                            borderRadius: 1,
                            bgcolor: `var(--mui-palette-${alert.type}-lightOpacity)`
                          }}
                        >
                          <IconifyIcon
                            icon={alertIcon}
                            width={16}
                            height={16}
                            color={`var(--mui-palette-background-main)`}
                          />
                        </Box>
                      }
                      sx={{
                        py: 1,
                        px: 2,
                        fontSize: '0.75rem',
                        alignItems: 'center',
                        cursor: isPaymentAlert ? 'pointer' : 'default',
                        '&:hover': isPaymentAlert ? {
                          bgcolor: 'action.hover'
                        } : {},
                        '& .MuiAlert-message': {
                          fontSize: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: 24
                        },
                        '& .MuiAlert-icon': {
                          padding: 0,
                          marginRight: 1
                        }
                      }}
                      onClick={isPaymentAlert ? () => {
                        // Scroll para o componente de pagamentos
                        const paymentsElement = document.querySelector('[data-testid="upcoming-payments"]')

                        if (paymentsElement) {
                          paymentsElement.scrollIntoView({ behavior: 'smooth' })
                        }
                      } : undefined}
                    >
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                          {alert.title}
                        </Typography>
                        <br />
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                          {alert.message}
                        </Typography>
                      </Box>
                    </Alert>
                  )
                })}
              </Stack>
            </Box>
          )}

          {/* KPIs Compactos */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.75rem' }}>
              {t('insights.keyIndicators')}
            </Typography>
            <Stack spacing={1.5}>
              {insightsData.map((insight, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        p: 0.5,
                        borderRadius: 0.5,
                        bgcolor: `var(--mui-palette-${insight.color}-lightOpacity)`,
                        color: `var(--mui-palette-${insight.color}-main)`
                      }}
                    >
                      <IconifyIcon icon={insight.icon} width={20} height={20} />
                    </Box>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                      {insight.title}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {insight.value}
                    </Typography>
                    {insight.trend !== 0 && (
                      <Chip
                        label={`${insight.trend > 0 ? '+' : ''}${insight.trend}%`}
                        size="small"
                        color={insight.trend > 0 ? 'success' : 'error'}
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          '& .MuiChip-label': { px: 0.5 }
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Progresso de Metas */}
          {progressData.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.75rem' }}>
                {t('insights.goalsProgress')}
              </Typography>
            <Stack spacing={1.5}>
              {progressData.map((item, index) => {
                // Os dados já vêm em percentual da API
                const percentage = item.current
                const isOverTarget = percentage > 100

                return (
                  <Box key={index}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                        {item.label}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                        {percentage.toFixed(1)}{item.unit}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(percentage, 100)}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'action.hover',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: isOverTarget ? 'success.main' :
                                   percentage > 75 ? 'success.main' :
                                   percentage > 50 ? 'warning.main' : 'error.main',
                          borderRadius: 3
                        }
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.7rem',
                        color: isOverTarget ? 'success.main' : 'text.secondary'
                      }}
                    >
                      {percentage.toFixed(1)}% {t('insights.goalProgress')}
                    </Typography>
                  </Box>
                )
              })}
            </Stack>
            </Box>
          )}


        </Stack>
      </CardContent>
    </Card>
  )
}

export default QuickInsightsWidget
